import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Pencil, Trash2, Paperclip, Upload, FileText, ImageIcon, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", problem: "", solution: "", tags: "", category: "Geral" };

function humanSize(bytes) {
  if (bytes == null) return "";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes, u = 0;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u++; }
  return `${v.toFixed(v >= 10 || u === 0 ? 0 : 1)} ${units[u]}`;
}

function isImage(ct) { return ct && ct.startsWith("image/"); }

function AttachmentThumb({ att }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let alive = true; let url;
    if (isImage(att.content_type)) {
      api.get(`/files/${att.file_id}`, { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); if (alive) setBlobUrl(url); })
        .catch(() => {});
    }
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [att.file_id, att.content_type]);

  if (isImage(att.content_type) && blobUrl) {
    return <img src={blobUrl} alt={att.filename} className="h-12 w-12 rounded object-cover" />;
  }
  return (
    <div className="flex h-12 w-12 items-center justify-center rounded bg-muted text-muted-foreground">
      {isImage(att.content_type) ? <ImageIcon className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
    </div>
  );
}

async function openAttachment(att) {
  try {
    const r = await api.get(`/files/${att.file_id}`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
  }
}

export default function KnowledgeBase() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]); // for new articles
  const fileInputRef = useRef(null);

  const canEdit = user.role === "admin" || user.role === "tecnico";

  const load = async () => {
    try { const r = await api.get("/kb"); setArticles(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setPendingFiles([]); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...a, tags: (a.tags || []).join(", ") }); setPendingFiles([]); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      let articleId = editing?.id;
      if (editing) {
        await api.patch(`/kb/${editing.id}`, payload);
      } else {
        const r = await api.post("/kb", payload);
        articleId = r.data.id;
      }
      // Upload any pending files (new article flow)
      if (pendingFiles.length > 0 && articleId) {
        setUploading(true);
        for (const f of pendingFiles) {
          const fd = new FormData();
          fd.append("file", f);
          try {
            await api.post(`/kb/${articleId}/attachments`, fd,
              { headers: { "Content-Type": "multipart/form-data" } });
          } catch (err) {
            toast.error(`Falha ao enviar ${f.name}`);
          }
        }
        setUploading(false);
      }
      toast.success("Salvo");
      setOpen(false); setPendingFiles([]);
      load();
    } catch (e) {
      setUploading(false);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (a) => {
    if (!confirm(`Excluir "${a.title}"?`)) return;
    try { await api.delete(`/kb/${a.id}`); toast.success("Excluído"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const uploadNow = async (files) => {
    if (!editing) {
      // Just queue for after save
      setPendingFiles((prev) => [...prev, ...files]);
      return;
    }
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/kb/${editing.id}/attachments`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) {
        toast.error(`Falha ao enviar ${f.name}: ${formatApiErrorDetail(e.response?.data?.detail) || e.message}`);
      }
    }
    setUploading(false);
    // Refresh editing state with latest attachments
    try {
      const r = await api.get("/kb");
      setArticles(r.data);
      const fresh = r.data.find((x) => x.id === editing.id);
      if (fresh) setEditing(fresh);
    } catch { /* silent */ }
    toast.success("Arquivos enviados");
  };

  const removeAttachment = async (article, att) => {
    if (!confirm(`Remover "${att.filename}"?`)) return;
    try {
      await api.delete(`/kb/${article.id}/attachments/${att.file_id}`);
      toast.success("Anexo removido");
      const r = await api.get("/kb");
      setArticles(r.data);
      if (editing && editing.id === article.id) {
        const fresh = r.data.find((x) => x.id === article.id);
        if (fresh) setEditing(fresh);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const handleFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    uploadNow(files);
    e.target.value = "";
  };

  const filtered = articles.filter((a) =>
    !search || a.title.toLowerCase().includes(search.toLowerCase())
    || a.problem.toLowerCase().includes(search.toLowerCase())
    || (a.tags || []).some((t) => t.toLowerCase().includes(search.toLowerCase()))
  );

  const currentAtts = (editing?.attachments || []);

  return (
    <div className="space-y-8" data-testid="kb-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Documentação</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight">Base de Conhecimento</h1>
          <p className="mt-2 text-sm text-muted-foreground">Problemas, soluções e arquivos anexos.</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPendingFiles([]); }}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} data-testid="new-kb-btn"><Plus className="mr-2 h-4 w-4" /> Novo artigo</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Editar artigo" : "Novo artigo"}</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div><Label>Título</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="kb-title-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Categoria</Label>
                    <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="kb-category-input" /></div>
                  <div><Label>Tags (separadas por vírgula)</Label>
                    <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="kb-tags-input" /></div>
                </div>
                <div><Label>Problema</Label>
                  <Textarea required rows={4} value={form.problem} onChange={(e) => setForm({ ...form, problem: e.target.value })} data-testid="kb-problem-input" /></div>
                <div><Label>Solução</Label>
                  <Textarea required rows={5} value={form.solution} onChange={(e) => setForm({ ...form, solution: e.target.value })} data-testid="kb-solution-input" /></div>

                <div className="border-t border-border pt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <Label className="flex items-center gap-2"><Paperclip className="h-4 w-4" /> Anexos</Label>
                    <input ref={fileInputRef} type="file" multiple className="hidden"
                      accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.md,.zip"
                      onChange={handleFilePick} data-testid="kb-file-input" />
                    <Button type="button" size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}
                      disabled={uploading} data-testid="kb-upload-btn">
                      {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                      Enviar arquivos
                    </Button>
                  </div>
                  <p className="mb-2 text-xs text-muted-foreground">Máx. 25MB por arquivo. Suporta imagens, PDF, planilhas, documentos e mais.</p>

                  {/* Pending files for new article */}
                  {!editing && pendingFiles.length > 0 && (
                    <div className="space-y-1">
                      {pendingFiles.map((f, i) => (
                        <div key={i} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <span className="truncate">{f.name}</span>
                            <span className="text-xs text-muted-foreground">({humanSize(f.size)})</span>
                          </div>
                          <Button type="button" size="icon" variant="ghost"
                            onClick={() => setPendingFiles((p) => p.filter((_, idx) => idx !== i))}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))}
                      <p className="text-xs text-muted-foreground italic">Serão enviados ao salvar o artigo.</p>
                    </div>
                  )}

                  {/* Existing attachments when editing */}
                  {editing && currentAtts.length > 0 && (
                    <div className="space-y-1" data-testid="kb-attachments-list">
                      {currentAtts.map((att) => (
                        <div key={att.file_id}
                          className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm"
                          data-testid={`att-row-${att.file_id}`}
                        >
                          <button
                            type="button"
                            className="flex items-center gap-3 min-w-0 flex-1 text-left"
                            onClick={() => openAttachment(att)}
                          >
                            <AttachmentThumb att={att} />
                            <div className="min-w-0">
                              <div className="truncate">{att.filename}</div>
                              <div className="text-xs text-muted-foreground">{humanSize(att.size)} · {att.content_type}</div>
                            </div>
                          </button>
                          <Button type="button" size="icon" variant="ghost"
                            onClick={() => removeAttachment(editing, att)}
                            data-testid={`att-remove-${att.file_id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {editing && currentAtts.length === 0 && (
                    <p className="text-xs text-muted-foreground italic">Nenhum anexo ainda.</p>
                  )}
                </div>

                <DialogFooter>
                  <Button type="submit" disabled={uploading} data-testid="kb-save-btn">
                    {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Enviando...</> : "Salvar"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Buscar artigos..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="kb-search" />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filtered.length === 0 && (
          <p className="col-span-full text-center text-sm text-muted-foreground py-10">Nenhum artigo.</p>
        )}
        {filtered.map((a) => (
          <Card key={a.id} className="animate-in-up border-border hover:shadow-sm" data-testid={`kb-card-${a.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="font-heading text-lg leading-snug">{a.title}</CardTitle>
                {canEdit && (
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(a)} data-testid={`edit-kb-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    {user.role === "admin" && (
                      <Button size="icon" variant="ghost" onClick={() => remove(a)} data-testid={`delete-kb-${a.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                <Badge variant="outline">{a.category}</Badge>
                {(a.tags || []).map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
                {(a.attachments || []).length > 0 && (
                  <Badge variant="secondary" className="flex items-center gap-1 text-[10px]" data-testid={`kb-att-count-${a.id}`}>
                    <Paperclip className="h-3 w-3" /> {a.attachments.length}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Problema</div>
                <p className="mt-1 line-clamp-3 text-muted-foreground">{a.problem}</p>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Solução</div>
                <p className="mt-1 line-clamp-4">{a.solution}</p>
              </div>

              {(a.attachments || []).length > 0 && (
                <div className="border-t border-border pt-3">
                  <div className="mb-2 text-xs uppercase tracking-[0.15em] text-muted-foreground">Anexos</div>
                  <div className="flex flex-wrap gap-2">
                    {a.attachments.map((att) => (
                      <button
                        key={att.file_id}
                        type="button"
                        onClick={() => openAttachment(att)}
                        className="group flex items-center gap-2 rounded-md border border-border px-2 py-1 text-xs transition-colors hover:bg-accent"
                        data-testid={`kb-att-${att.file_id}`}
                      >
                        {isImage(att.content_type)
                          ? <ImageIcon className="h-3.5 w-3.5" />
                          : <FileText className="h-3.5 w-3.5" />}
                        <span className="max-w-[140px] truncate">{att.filename}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
